import { useState } from "react";
import { CapabilityCard } from "@/components/org/connections/CapabilityCard";
import { CT } from "@/lib/connectionsTheme";

// Left-to-right Connections layout: a scalable grid of category cards on
// the left (drag-reorderable by org admins). The Nova Opsis logo is a purely
// decorative, viewport-fixed watermark on the right — it never moves when the page scrolls.
export const ConnectionsPipeline = ({ categories, statusMap, onSetup, highlightKey, emptyMessage, onReorder, reorderDisabled }) => {
  const [draggedKey, setDraggedKey] = useState(null);

  const handleDrop = (targetKey) => {
    if (!draggedKey || draggedKey === targetKey || reorderDisabled) {
      setDraggedKey(null);
      return;
    }
    const keys = categories.map((c) => c.key);
    const from = keys.indexOf(draggedKey);
    const to = keys.indexOf(targetKey);
    setDraggedKey(null);
    if (from === -1 || to === -1) return;
    const next = [...keys];
    next.splice(from, 1);
    next.splice(to, 0, draggedKey);
    onReorder?.(next);
  };

  return (
    <div className="relative flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-6" data-testid="connections-pipeline">
      <div className="relative z-10 min-w-0 flex-1 lg:w-[65%]">
        {categories.length === 0 ? (
          <div className={`${CT.panel} p-10 text-center text-sm font-semibold text-slate-500 dark:text-slate-400`} data-testid="org-connections-no-results">
            {emptyMessage || "No connections match your search."}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="org-connections-grid">
            {categories.map((cap) => (
              <CapabilityCard
                key={cap.key}
                capability={cap}
                subStatuses={statusMap[cap.key] || {}}
                onSetup={onSetup}
                highlighted={highlightKey === cap.key}
                anchorId={`org-connections-anchor-${cap.key}`}
                dragDisabled={reorderDisabled}
                isDragging={draggedKey === cap.key}
                onDragStart={(e) => { setDraggedKey(cap.key); e.dataTransfer.effectAllowed = "move"; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleDrop(cap.key); }}
                onDragEnd={() => setDraggedKey(null)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="hidden lg:block lg:w-[35%]" aria-hidden="true" />

      <div
        className="pointer-events-none fixed right-[8%] top-1/2 z-0 hidden -translate-y-1/2 lg:block"
        aria-hidden="true"
        data-testid="connections-hub"
      >
        <img
          src="/nova-logo-transparent.png"
          alt=""
          className="h-[576px] w-[576px] object-contain opacity-[0.08] dark:opacity-[0.14] sm:h-[672px] sm:w-[672px]"
          data-testid="connections-hub-logo"
        />
      </div>
    </div>
  );
};
